/**
 * Most of the utility function have `strict` as an optional argument,
 * which is used to `throw error` instead of handling it within the function
 */

/**
 * @exports getValueFromNestedObject  Get value from an object by passing string pointing to key
 * @param {Object} obj Source object
 * @param {string | string[]} keyPath String/array pointing to exact key
 * @param {Object} optional
 * @param {boolean | true} optional.strict Throws error when the arguments are invalid
 * @returns {any}
 * @example getValueOfNestedObject({a:{b:{c:'hello'}}},'a.b.c') => 'hello'
 */
export function getValueFromNestedObject(obj, keyPath, { strict = true } = {}) {
    const err_msg =
        "getValueOfNestedObject() Failed to get value of the nested obj";
    obj = parseJSON(obj, { strict: false });
    if (typeof obj !== "object" || Object.keys(obj).length === 0) {
        if (strict)
            throw new Error(err_msg, {
                cause: `TYPE_ERROR: Invalid arguments. Obj has to be an object and cannot be empty. Passed value for obj: '${JSON.stringify(obj)}'`,
            });
        return;
    }
    if (Array.isArray(keyPath)) {
        keyPath = keyPath.join(".");
    }
    // Validating the keys parameter
    else if (typeof keyPath != "string" && typeof keys != "number") {
        if (strict)
            throw new Error(err_msg, {
                cause: `TYPE_ERROR: Invalid arguments. keyPath has to be an 'string|string[]|number' and cannot be empty. Passed value for keys: '${JSON.stringify(keyPath)}'`,
            });
        return;
    }
    const keysArr = keyPath.toString().split(".");
    const returnValue = keysArr.reduce(
        (acc, curr, index, arr) =>
            index === arr.length - 1 ? acc[curr] : acc[curr] || {},
        obj
    );
    return returnValue;
}

/**
 * @exports setValueToNestedObject  Set a value to a object by pointing to the exact key location and value to set to that key
 * @param {Object} obj Source object
 * @param {any} assignValue Value to be assigned to that function
 * @param {string | string[]} keys String/array pointing to exact key
 * @param {Object} optional
 * @param {boolean | true} optional.strict Throws error when the arguments are invalid
 * @param {boolean | true} optional.clone_obj Clone a new object and don't change value to the existing object
 * @returns {any}
 * @example setValueToNestedObject({},'a.b.c','hello') => {a:{b:{c:'hello'}}}
 */
export function setValueToNestedObject(
    obj,
    keyPath,
    assignValue,
    { strict = true, clone_obj = true } = {}
) {
    const err_msg =
        "setValueToNestedObject() Failed to get value of the nested obj";
    obj = parseJSON(obj, { strict: false });
    if (typeof obj !== "object") {
        if (strict)
            throw new Error(err_msg, {
                cause: `TYPE_ERROR: Invalid arguments. 'obj' has to be an object and cannot be empty. Passed value for obj: '${obj}'`,
            });
        return;
    }
    if (Array.isArray(keyPath)) {
        keyPath = keyPath.join(".");
    }
    // Validating the keys parameter
    else if (typeof keyPath != "string" && typeof keyPath != "number") {
        if (strict)
            throw new Error(err_msg, {
                cause: `TYPE_ERROR: Invalid arguments. 'keyPath' has to be an 'string|string[]|number' and cannot be empty. Passed value for keyPath: '${keyPath}'`,
            });
        return;
    }
    if (clone_obj) obj = structuredClone(obj);
    const keysArr = keyPath.toString().split(".");
    // In each iteration of the loop, we're drilling down into the object,
    // creating an empty object if key value is not an object(if needed). 
    // Once we reach the last key, we assign the given value to that key.
    keysArr.reduce((acc, curr, index) => {
        // If the current key doesn't exist in the object or isn't an object itself,
        // and we're not at the last key, initialize it as an empty object.
        if ((!acc.hasOwnProperty(curr) || typeof acc[curr] != 'object') && index < keysArr.length - 1) {
            acc[curr] = {};
        } else if (index === keysArr.length - 1) {
            // If we're at the last key, assign the value to this key.
            acc[curr] = assignValue;
        }
        return acc[curr]; // Return the nested object to continue drilling down.
    }, obj);
    return obj;
}

/**
 * @exports retrieveKeysFromObj Retrieve all the keys from an object including the objects inside an array
 * @param {any} obj
 * @param {string[]} retrieved_keys Stores all the keys that are retrieved from object. Don't pass any values while call this function
 * @returns {string[]}
 */
export function retrieveKeysFromObj(obj, retrieved_keys = []) {
    obj = parseJSON(JSON.stringify(obj)); // This removes the function values from the object
    // When the passed argument is not an object just returning the `undefined`. Throwing error would cause an issue since its a recursive function
    if (typeof obj != "object") return;
    const currentKeys = Object.keys(obj);
    retrieved_keys.push(...currentKeys);
    for (let i = 0; i < currentKeys.length; i++) {
        const currentValue = obj[currentKeys[i]];
        // Check if object has any nested object
        if (typeof currentValue === "object") {
            // Checking if the nested object has any array inside it
            if (Array.isArray(currentValue)) {
                currentValue.forEach((value) => {
                    retrieveKeysFromObj(value, retrieved_keys);
                });
            } else {
                retrieveKeysFromObj(currentValue, retrieved_keys);
            }
        }
    }
    return retrieved_keys;
}

/**
 * @exports parseJSON Utility function that parse the string without throwing error
 * @param {any} text
 * @param {any}
 */
export function parseJSON(text, { strict = true } = {}) {
    try {
        if (typeof text === "object") return text;
        if (typeof text === "string") {
            return JSON.parse(text);
        }
        throw new Error("parseJSON() Failed to parse the given text", {
            cause: `TYPE_ERROR: Invalid type passed as an argument, must 'arg' be a 'string|object'. Passed type:'${typeof text}'`,
        });
    } catch (error) {
        if (strict) throw error;
        return text;
    }
}

/**
 * @function isPlainObject Check if the entered value is an object with keys in them
 * @param {any} input 
 * @param {boolean} isEmptyCheck 
 * @returns {boolean}
 */
export function isPlainObject(input, isEmptyCheck = true) {
    let isObject = input && !Array.isArray(input) && typeof input === 'object';
    if (isEmptyCheck) {
        return !!(isObject && Object.keys(input).length);
    } else {
        return isObject;
    }
}